/**
 * Everything the screen turns a number into words with.
 *
 * In one file because the same duration is rendered in a table cell, a detail header and
 * a transcript's jump column, and three implementations of "4m 57s" drift into three
 * different answers for the same call.
 */

/** `4m 57s`, never `297000ms`. Nobody reads a call length in milliseconds. */
export function duration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return "—";
  const total = Math.round(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return minutes ? `${minutes}m ${String(seconds).padStart(2, "0")}s` : `${seconds}s`;
}

/** `1:42`, the form a transcript's jump column and an audio player both use. */
export function offset(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return "";
  const total = Math.floor(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

/*
 * TIMES ARE PRINTED IN THE BUSINESS'S CLOCK, NOT THE READER'S.
 *
 * An owner in London looking at their Sydney boat shop needs "Mon, 31 Aug, 10:14" to mean
 * 10:14 in the shop - the hour the heatmap put it in and the hour their staff were there.
 * Rendering it in the browser's zone would put the same call two hours earlier on the
 * detail page than on the chart, with nothing on either to say why.
 *
 * `timeZone: undefined` is the browser's own zone, which is the honest fallback when a
 * business has no timezone set: NULL means "we do not know where this is", and guessing
 * would be the same mistake as defaulting the local columns to UTC.
 *
 * The formatters are cached by zone because constructing an `Intl.DateTimeFormat` is not
 * cheap and a fifty-row table calls this fifty times per render.
 */
const days = new Map<string, Intl.DateTimeFormat>();
const times = new Map<string, Intl.DateTimeFormat>();
const fulls = new Map<string, Intl.DateTimeFormat>();

function formatter(
  cache: Map<string, Intl.DateTimeFormat>,
  timeZone: string | null | undefined,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = timeZone ?? "";
  let found = cache.get(key);
  if (!found) {
    try {
      found = new Intl.DateTimeFormat(undefined, { ...options, timeZone: timeZone ?? undefined });
    } catch {
      // A zone this browser does not know. The Worker refuses one on save, so this is only
      // reachable from an older row - and a time in the reader's own clock beats a crash.
      found = new Intl.DateTimeFormat(undefined, options);
    }
    cache.set(key, found);
  }
  return found;
}

export function when(epochMs: number, timeZone?: string | null): string {
  const date = new Date(epochMs);
  const day = formatter(days, timeZone, { weekday: "short", day: "numeric", month: "short" });
  const time = formatter(times, timeZone, { hour: "numeric", minute: "2-digit" });
  // Compared as FORMATTED DAYS IN THAT ZONE rather than with `toDateString`, which is
  // always the browser's: at 9am in London it is already tomorrow in Sydney, and "today"
  // has to mean today where the business is or the bare time is the wrong day's.
  const sameDay = day.format(date) === day.format(new Date());
  return sameDay ? time.format(date) : `${day.format(date)}, ${time.format(date)}`;
}

export function fullWhen(epochMs: number, timeZone?: string | null): string {
  return formatter(fulls, timeZone, { dateStyle: "full", timeStyle: "short" }).format(
    new Date(epochMs),
  );
}

/*
 * THE DAY A CALL BELONGS TO, which is a different question from what time it happened.
 *
 * `/calls` groups under one heading per day, so it needs two things `when()` does not
 * give it: a key that two calls on the same day agree on, and a heading to print over
 * them. Both are computed IN THE BUSINESS'S CLOCK for the reason `when` states above -
 * at 9am in London it is already tomorrow in Sydney, and a Sydney boat shop's calls have
 * to fall under the day its staff worked.
 */
const keys = new Map<string, Intl.DateTimeFormat>();
const longDays = new Map<string, Intl.DateTimeFormat>();
const longDated = new Map<string, Intl.DateTimeFormat>();

/**
 * `2026-08-29` - the calendar date in that zone, assembled rather than parsed.
 *
 * `formatToParts` and not a locale that happens to produce ISO: `en-CA` does today and is
 * not promised to tomorrow, and `toDateString` is always the BROWSER's zone, which is the
 * exact mistake this file spends twenty lines warning about.
 */
export function dayKey(epochMs: number, timeZone?: string | null): string {
  const parts = formatter(keys, timeZone, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(epochMs));
  const find = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
  return `${find("year")}-${find("month")}-${find("day")}`;
}

/** The day before a `dayKey`, as a `dayKey`. */
function dayBefore(key: string): string {
  const [year, month, day] = key.split("-").map(Number) as [number, number, number];
  /*
   * CALENDAR ARITHMETIC, NOT `Date.now() - 86_400_000`. On the 23-hour day a zone springs
   * forward, subtracting a fixed day from a timestamp lands on the day before yesterday
   * and the heading lies - once a year, in one hemisphere, which is precisely the bug
   * nobody finds. UTC midnight has no such day: every UTC day is 24 hours long.
   */
  const previous = new Date(Date.UTC(year, month - 1, day) - 86_400_000);
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${previous.getUTCFullYear()}-${pad(previous.getUTCMonth() + 1)}-${pad(previous.getUTCDate())}`;
}

/**
 * `Today`, `Yesterday`, or `Friday, 29 August` - the heading over one day's calls.
 *
 * The year appears only when it is not this one. A list paged back fourteen months must
 * not head a group with a bare "Friday, 29 August", and printing 2026 on every heading in
 * a list that is almost always this week is noise on every row to be right on one.
 */
export function dayLabel(epochMs: number, timeZone?: string | null): string {
  const key = dayKey(epochMs, timeZone);
  const today = dayKey(Date.now(), timeZone);
  if (key === today) return "Today";
  if (key === dayBefore(today)) return "Yesterday";

  const sameYear = key.slice(0, 4) === today.slice(0, 4);
  return formatter(sameYear ? longDays : longDated, timeZone, {
    weekday: "long",
    day: "numeric",
    month: "long",
    ...(sameYear ? {} : { year: "numeric" }),
  }).format(new Date(epochMs));
}

/** `10:42` - the time alone, for a row whose day is already stated above it. */
export function timeOfDay(epochMs: number, timeZone?: string | null): string {
  return formatter(times, timeZone, { hour: "numeric", minute: "2-digit" }).format(
    new Date(epochMs),
  );
}

/*
 * A DAY SAID THE WAY A PERSON SAYS IT, for a row that holds its own date rather than
 * sitting under a heading. The simplified register has no day headings: every row is one
 * call at a counter, so the row says "Yesterday" or "Monday" over its time, and says nothing
 * at all for today, where the time alone is the answer.
 */
const weekdays = new Map<string, Intl.DateTimeFormat>();
const shortDates = new Map<string, Intl.DateTimeFormat>();
const shortDated = new Map<string, Intl.DateTimeFormat>();
const barDates = new Map<string, Intl.DateTimeFormat>();

/**
 * `""` for today, `Yesterday`, `Monday` inside the last week, then `12 Sep`.
 *
 * Counted in CALENDAR DAYS IN THE BUSINESS'S ZONE, stepping back with `dayBefore` rather
 * than dividing a difference in milliseconds by a day - the same daylight-saving argument
 * `dayBefore` makes. The year appears only when it is not this one, as in `dayLabel`.
 */
export function shortDay(epochMs: number, timeZone?: string | null): string {
  const key = dayKey(epochMs, timeZone);
  let cursor = dayKey(Date.now(), timeZone);
  if (key === cursor) return "";
  for (let back = 1; back <= 6; back++) {
    cursor = dayBefore(cursor);
    if (key === cursor) {
      return back === 1
        ? "Yesterday"
        : formatter(weekdays, timeZone, { weekday: "long" }).format(new Date(epochMs));
    }
  }
  const sameYear = key.slice(0, 4) === dayKey(Date.now(), timeZone).slice(0, 4);
  return formatter(sameYear ? shortDates : shortDated, timeZone, {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
  }).format(new Date(epochMs));
}

/** `Tuesday 22 September`, the date the top bar prints beside the business's own clock. */
export function clockDate(epochMs: number, timeZone?: string | null): string {
  return formatter(barDates, timeZone, { weekday: "long", day: "numeric", month: "long" }).format(
    new Date(epochMs),
  );
}

/**
 * Money, with the basis it came from attached by the caller.
 *
 * The dashboard never renders a figure without saying what kind of figure it is. A
 * quote an agent read out over the phone is not revenue, and a column headed "value"
 * that silently mixes the two is how a product starts lying to an owner about money
 * their business never made.
 */
export function money(minor: number, currency: string | null): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency ?? "AUD",
    maximumFractionDigits: 0,
  }).format(minor / 100);
}

const BASIS_LABELS: Record<string, string> = {
  indicative_quote: "quoted",
  booked: "booked",
  estimate: "estimated",
};

export function basisLabel(basis: string | null): string {
  return basis ? (BASIS_LABELS[basis] ?? basis.replace(/_/g, " ")) : "";
}

/** `hire_referral` reads as `hire referral`; a business's own label passes through. */
export function humanise(value: string | null | undefined): string {
  return (value ?? "").replace(/_/g, " ");
}

/**
 * Australian mobile numbers are unreadable as +61412345678 and fine as 0412 345 678.
 *
 * RETURNS NULL, NOT "", when there is no number. Every caller of this function writes
 * `name ?? phone(n) ?? "Unknown caller"`, and `??` does not fire on an empty string - so
 * an empty return made the fallback dead code and rendered a caller with no name and no
 * number as blank space next to an avatar.
 */
export function phone(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const digits = raw.replace(/[^\d+]/g, "");
  const local = digits.startsWith("+61") ? `0${digits.slice(3)}` : digits;
  return /^04\d{8}$/.test(local)
    ? `${local.slice(0, 4)} ${local.slice(4, 7)} ${local.slice(7)}`
    : raw;
}

/**
 * The customer's number on a call, or null: the one a person reading the call would dial.
 *
 * On a call the agent PLACED, `caller` is the business's own number and `callee` is the
 * customer's, so the one worth showing is whichever the direction makes true. Falling back
 * rather than branching: an inbound call has no callee, so the order alone gets both cases
 * right, and a detail with neither still reaches the number the agent took down.
 *
 * ONE RULE FOR THREE SCREENS. The call dialog's header, the simplified register's row and
 * its call pane all title a call by this; written out three times it would be three chances
 * for one of them to put the shop's own line in front of somebody about to tap it.
 */
export function numberOf(call: {
  direction: string | null;
  caller: string | null;
  callee: string | null;
  contact?: { phone?: string | null } | { name: string | null; location: string | null };
}): string | null {
  const dialled = call.direction === "outbound" ? (call.callee ?? call.caller) : call.caller;
  const taken = call.contact && "phone" in call.contact ? call.contact.phone : null;
  return dialled ?? taken ?? null;
}

/**
 * A call's handle: the last four characters of its own id, which is what an owner reads a
 * row by until there is a real number on it.
 *
 * The log used to title a row with `outcome.contact.name`, and a name is the one field on
 * a call that cannot be derived - it is only ever what the caller said. When an agent's
 * model filled an optional `caller_name` slot while doing something else, "mate" became
 * the title of a call nobody had given a name on.
 *
 * Sliced rather than hashed so that what is on the screen is still what is in the record -
 * `search` matches it with a LIKE, and a hash would have made the one identifier a reader
 * can see the one they cannot look up. Call ids are organization-scoped and already human-shaped
 * ("HA-2609-9702", "EB-2608-5158"), so four of their characters read as a reference and
 * cannot be mistaken for a phone number.
 */
export function handle(callId: string): string {
  const alnum = callId.replace(/[^a-zA-Z0-9]/g, "");
  return `Caller ${(alnum.slice(-4) || callId).toUpperCase()}`;
}

/**
 * Micro-dollars, at a precision that suits the size of the number.
 *
 * Cost per call is fractions of a cent and a total across organizations is hundreds of
 * dollars, and one format cannot serve both: `$0.00` for a call tells you nothing, and
 * `$402.3550` for a month is noise pretending to be precision. INTERNAL ONLY - nothing
 * formatted by this function has ever been shown to a member and nothing ever will be.
 */
export function usd(micros: number | null | undefined): string {
  if (micros === null || micros === undefined) return "—";
  const dollars = micros / 1_000_000;
  const digits = Math.abs(dollars) < 1 ? 4 : Math.abs(dollars) < 100 ? 2 : 0;
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(dollars);
}

/** Basis points as a percentage: 5000 reads as 50%. */
export function percent(bps: number | null | undefined): string {
  return bps === null || bps === undefined ? "—" : `${Math.round(bps / 100)}%`;
}

/**
 * `+12` / `-3` / nothing. A number without a direction is a number nobody can act on.
 *
 * Silent when the previous period is empty, rather than reporting `+29`. An agent that did
 * not exist last month has not grown by twenty-nine calls, and a comparison against
 * nothing is the kind of figure that ends up in a sentence beginning "we're up".
 */
export function delta(now: number, before: number): string {
  if (!before) return "";
  const change = now - before;
  return change === 0 ? "no change" : `${change > 0 ? "+" : ""}${change}`;
}

/** `410ms`, or `1.9s` once milliseconds stop being readable. */
export function ms(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return value >= 1000 ? `${(value / 1000).toFixed(1)}s` : `${Math.round(value)}ms`;
}

/**
 * The business's own word for something, or ours humanised.
 *
 * The dashboard renders whatever the agent called an outcome, verbatim: a boat shop's
 * owner should read their vocabulary and not ours. `labels` in the taxonomy is a display
 * override for the cases where a machine-readable label reads badly to a human -
 * `no_show` on a screen should say "No-show" - and never a mapping that changes what a
 * label means.
 */
export function labelFor(
  overrides: Record<string, string> | undefined,
  value: string | null | undefined,
): string {
  if (!value) return "—";
  return overrides?.[value] ?? humanise(value);
}

/** `41%`. Null when there was nothing to divide, which is not the same as zero. */
export function share(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : `${Math.round(value * 100)}%`;
}

/**
 * `+12 on the 30 days before` / nothing.
 *
 * Silent when the previous period is empty. An agent that did not exist last month has
 * not grown by twenty-nine calls, and a comparison against nothing is the kind of figure
 * that ends up in a sentence beginning "we're up".
 */
export function against(now: number, before: number, days: number): string {
  if (!before) return "";
  const change = now - before;
  if (change === 0) return `same as the ${days} days before`;
  return `${change > 0 ? "+" : ""}${change} on the ${days} days before`;
}

/** `1,240` — a count somebody reads rather than parses. */
export function count(value: number): string {
  return value.toLocaleString();
}

/* --- arbitrary JSON, made readable --------------------------------------------------
 *
 * `outcome.fields` is the one place the platform stops caring what the shape is: a boat
 * shop puts `motorKwRequired` in it, a dentist puts `lastVisitMonths`. These two
 * functions are what let one renderer read both without the platform learning a word
 * about either. They work on the KEY, never on a list of known field names, so an agent
 * that ships a new field on Tuesday gets it rendered properly on Tuesday.
 */

// Tokens that must not be sentence-cased into "Kw" and "Kwh". Everything else in a key
// is an ordinary word.
const TOKENS: Record<string, string> = {
  kw: "kW",
  kwh: "kWh",
  ft: "ft",
  hp: "hp",
  id: "ID",
  kg: "kg",
  km: "km",
  sms: "SMS",
  url: "URL",
  ms: "ms",
};

/**
 * The unit a key implies, and the part of the key that says so.
 *
 * `lengthFt: 34` should read "Length · 34 ft", not "lengthFt · 34". The suffix is both
 * the unit AND noise in the label, so it is removed from one and shown in the other -
 * which is the whole reason this returns a pair rather than a string.
 *
 * Suffix-matched on purpose. A key called `draft` is not a unit and must pass through
 * untouched; only `somethingFt` claims to be feet.
 */
const UNITS: Array<[string, string]> = [
  ["Kwh", "kWh"],
  ["Kw", "kW"],
  ["Ft", "ft"],
  ["Hp", "hp"],
  ["Knots", "kn"],
  ["Tonnes", "t"],
  ["Hours", "h"],
  ["Minutes", "min"],
  ["Seconds", "s"],
  ["PerWeek", "/week"],
  ["PerDay", "/day"],
  ["Pct", "%"],
  ["Percent", "%"],
  ["Ms", "ms"],
];

/**
 * CASE-SENSITIVE, and that is the whole correctness of this function.
 *
 * A case-insensitive `ft$` turns `draft` into "Dra" measured in feet. The suffix only
 * counts where it starts a word: a capital in camelCase, or after an underscore. So
 * `lengthFt` and `length_ft` are feet, and `draft` is a draft.
 */
const UNIT_PATTERNS: Array<[RegExp, string]> = UNITS.map(([token, suffix]) => [
  new RegExp(`^(.+?)(?:${token}|_${token.toLowerCase()})$`),
  suffix,
]);

export function unit(key: string): { stem: string; suffix: string } | null {
  for (const [pattern, suffix] of UNIT_PATTERNS) {
    const match = pattern.exec(key);
    // A key that is ONLY a unit ("ft") is a field called feet, not a suffix on nothing.
    if (match?.[1]) return { stem: match[1], suffix };
  }
  return null;
}

/**
 * A key from somebody else's JSON, as a label a business owner can read.
 *
 * `motorKwRequired` becomes "Motor kW required"; `owns_boat` becomes "Owns boat". Only
 * the first word is capitalised, because a row of Title Case Labels reads as a form and
 * these are facts. `humanise` is left alone - it renders VALUES, where a business's own
 * word must survive exactly as they wrote it.
 */
export function label(key: string): string {
  const stem = unit(key)?.stem ?? key;
  const words = stem
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => TOKENS[word.toLowerCase()] ?? word.toLowerCase());

  if (!words.length) return key;
  const [first, ...rest] = words;
  // A token that carries its own casing ("kW") keeps it even in first position.
  const head = TOKENS[first!.toLowerCase()] === first ? first! : first![0]!.toUpperCase() + first!.slice(1);
  return [head, ...rest].join(" ");
}

/**
 * The NAME of a thing, in title case: `identify_patient` reads as "Identify Patient".
 *
 * TITLE CASE HERE, SENTENCE CASE IN `label`, and the difference is what each one is
 * naming. `label` renders the key of a fact - a row in a form, where Title Case On Every
 * Row Reads As Shouting - and this renders something the agent DID, which sits at the
 * head of its own card and behaves like a proper noun. It goes through `label` first, so
 * a unit token keeps its own casing: `check_kw_limit` is "Check kW Limit", not "Kw".
 */
const MINOR = new Set([
  "a", "an", "and", "as", "at", "by", "for", "from", "in", "of", "on", "or", "per", "the",
  "to", "with",
]);

export function title(key: string): string {
  return label(key)
    .split(" ")
    .map((word, index) => {
      const lower = word.toLowerCase();
      // A unit keeps its own casing, and a joining word keeps its lower case unless it
      // opens the name: "Size the System", not "Size The System".
      if (TOKENS[lower] === word) return word;
      if (index > 0 && MINOR.has(lower)) return lower;
      return word[0]!.toUpperCase() + word.slice(1);
    })
    .join(" ");
}

/** `2.5`, never `2.4999999999`. A rounded number is a readable one. */
export function round(value: number): string {
  return String(Math.round(value * 100) / 100);
}

/** `/week` hangs off the number; `kW` needs air. */
export function spaced(suffix: string): string {
  return suffix.startsWith("/") || suffix === "%" ? suffix : ` ${suffix}`;
}

/**
 * A calendar day as a person picked it, `YYYY-MM-DD`, as "Thu 1 Oct".
 *
 * READ IN UTC ON PURPOSE. The string is already the business's day - there is no instant to
 * move into a timezone - and reading midnight of it in the browser's zone would print the day
 * before for everybody west of Greenwich.
 */
export function dayOf(on: string): string {
  // The reader's locale, as `when` uses, so "Thu, Oct 1" sits beside "Sun, Sep 27" in one voice.
  return formatter(dayOfs, "UTC", { weekday: "short", day: "numeric", month: "short" }).format(
    new Date(`${on}T00:00:00Z`),
  );
}
const dayOfs = new Map<string, Intl.DateTimeFormat>();
